import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { env } from '../../env.js';

/**
 * Initialises browser-side OpenTelemetry tracing.
 *
 * No-ops when VITE_OTEL_COLLECTOR_URL is unset — safe in local dev and CI.
 * The FetchInstrumentation propagates W3C trace-context headers so backend
 * spans can be correlated with frontend spans.
 *
 * NOTE: Sentry already handles errors/exceptions. OTel handles traces only.
 * Sentry's fetch instrumentation is a separate concern; both can coexist.
 */
export function initBrowserTelemetry(): void {
  if (!env.VITE_OTEL_COLLECTOR_URL) return;

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: 'player-fe',
    [ATTR_SERVICE_VERSION]: env.VITE_APP_VERSION ?? 'dev',
  });

  // In @opentelemetry/sdk-trace-base v2, spanProcessors are passed via config
  // (addSpanProcessor was removed in v2).
  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(
        new OTLPTraceExporter({
          url: `${env.VITE_OTEL_COLLECTOR_URL}/v1/traces`,
        }),
      ),
    ],
  });

  provider.register();

  // FetchInstrumentation propagates trace-context headers to all backend calls
  // so a player action can be traced end-to-end across services.
  // clearTimingResources prevents the performance buffer from filling up.
  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: [/.+/],
        clearTimingResources: true,
      }),
    ],
  });
}
